import axios from "axios";
import { useSelector } from "react-redux";
import { baseURL } from "services/API";

const EINVOICING_BASE = `${baseURL}/einvoicing`;

const errorMessage = async (error, fallback) => {
  let data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      data = JSON.parse(await data.text());
    } catch (e) {
      return fallback;
    }
  }
  if (!data) return error?.message || fallback;
  if (typeof data === "string") return data;
  if (typeof data.error === "string") return data.error;
  if (data.detail) return data.detail;
  if (data.message) return data.message;
  return fallback;
};

const useEinvoicing = () => {
  const token = useSelector((state) => state.token);

  const authHeaders = () => {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  };

  /** Wraps a request as { success, data } / { success: false, message, data }. */
  const run = async (request, fallback) => {
    authHeaders();
    try {
      const response = await request();
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        message: await errorMessage(error, fallback),
        data: error?.response?.data,
      };
    }
  };

  const getConfig = async () => {
    authHeaders();
    const response = await axios.get(`${EINVOICING_BASE}/config`);
    return response.data;
  };

  const updateConfig = async (payload) => {
    authHeaders();
    const response = await axios.put(`${EINVOICING_BASE}/config`, payload);
    return response.data;
  };

  const testConnection = async () => {
    authHeaders();
    const response = await axios.post(`${EINVOICING_BASE}/test-connection`);
    return response.data;
  };

  const refreshAesKey = async () => {
    authHeaders();
    const response = await axios.post(`${EINVOICING_BASE}/refresh-aes-key`);
    return response.data;
  };

  const syncGoods = async () => {
    authHeaders();
    const response = await axios.post(`${EINVOICING_BASE}/goods/sync`);
    return response.data;
  };

  const listInvoices = async () => {
    authHeaders();
    const response = await axios.get(`${EINVOICING_BASE}/invoices`);
    return response.data;
  };

  const issueInvoice = async (payload) => {
    authHeaders();
    const response = await axios.post(`${EINVOICING_BASE}/invoices`, payload);
    return response.data;
  };

  // --- EFRIS self-onboarding (scoped to the logged-in landlord) ---

  const getStatus = () =>
    run(() => axios.get(`${EINVOICING_BASE}/status`), "Could not load EFRIS status.");

  const generateKeys = (force = false) =>
    run(
      () => axios.post(`${EINVOICING_BASE}/onboarding/generate-keys`, { force }),
      "Could not create your keys."
    );

  const saveDevice = (payload) =>
    run(
      () => axios.put(`${EINVOICING_BASE}/onboarding/device`, payload),
      "Could not save your TIN and device number."
    );

  const verifyConnection = () =>
    run(
      () => axios.post(`${EINVOICING_BASE}/onboarding/verify`, {}),
      "Could not connect to EFRIS."
    );

  const saveSettings = (payload) =>
    run(
      () => axios.put(`${EINVOICING_BASE}/onboarding/settings`, payload),
      "Could not save your settings."
    );

  const listDocuments = () =>
    run(() => axios.get(`${EINVOICING_BASE}/invoices`), "Could not load your EFRIS documents.");

  const getTenancies = () =>
    run(() => axios.get(`${EINVOICING_BASE}/tenancies`), "Could not load your tenants.");

  const issuePeriodInvoice = (payload) =>
    run(
      () => axios.post(`${EINVOICING_BASE}/period-invoices`, payload),
      "Could not issue the EFRIS invoice."
    );

  const downloadFile = async (path, filename, fallback) => {
    const result = await run(
      () => axios.get(`${EINVOICING_BASE}${path}`, { responseType: "blob" }),
      fallback
    );
    if (!result.success) return result;
    const href = window.URL.createObjectURL(result.data);
    const link = document.createElement("a");
    link.href = href;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(href);
    return { success: true };
  };

  const downloadPublicKey = (type = "crt") =>
    downloadFile(
      `/onboarding/public-key?type=${type}`,
      `efris_public_key.${type}`,
      "Could not download the key file."
    );

  const downloadInstructions = () =>
    downloadFile(
      "/onboarding/instructions",
      "efris_setup_instructions.pdf",
      "Could not download the instructions."
    );

  const downloadDocumentPdf = (doc) =>
    downloadFile(
      `/invoices/${doc.id}/pdf`,
      `efris_${doc.document_kind || "receipt"}_${doc.fiscal_document_number || doc.id}.pdf`,
      "Could not download the PDF."
    );

  return {
    getConfig,
    updateConfig,
    testConnection,
    refreshAesKey,
    syncGoods,
    listInvoices,
    issueInvoice,
    getStatus,
    generateKeys,
    saveDevice,
    verifyConnection,
    saveSettings,
    listDocuments,
    getTenancies,
    issuePeriodInvoice,
    downloadPublicKey,
    downloadInstructions,
    downloadDocumentPdf,
  };
};

export default useEinvoicing;
